// REQ-FE-029g — Reminder cadence + notify-comptable settings panel.
//
// Spec wanted these wired to `tenant.reminderCadenceDays` +
// `tenant.notifyComptableOnReminder` via the existing PATCH endpoint.
// As of 2026-06-02 neither field exists on the contract — REQ-BE-022
// added the validation-reminders cron but cadence is hardcoded
// server-side. We ship the visual scaffold matching mockup
// `08-tenant-settings.png` with controls disabled + a clear info note,
// so the screen looks right and the rest of the integration is a
// 5-line change once the contract fields land.
//
// Follow-up REQ-BE-N (logged in this REQ's REPORT block):
//   - Add Tenant.reminderCadenceDays: 3 | 7 | 14 (default 7)
//   - Add Tenant.notifyComptableOnReminder: boolean (default true)
//   - Extend PATCH /api/tenants/:id payload to accept both fields
//   - Update validation-reminders cron to read the per-tenant cadence

const CADENCE_OPTIONS: Array<{ value: 3 | 7 | 14; label: string }> = [
  { value: 3, label: 'Tous les 3 jours' },
  { value: 7, label: 'Tous les 7 jours' },
  { value: 14, label: 'Tous les 14 jours' },
];

interface Props {
  // Effective (read-only) cadence value to highlight. Default 7 matches
  // the server-side hardcode in REQ-BE-021/022 cron.
  effectiveCadenceDays?: 3 | 7 | 14;
  effectiveNotify?: boolean;
}

export function ReminderCadencePicker({ effectiveCadenceDays = 7, effectiveNotify = true }: Props) {
  return (
    <article
      className="invoice-detail__card"
      data-testid="settings-reminder-cadence"
      aria-label="Cadence des rappels de validation"
    >
      <div className="eyebrow">Validations · Cadence des rappels</div>
      <p className="muted" style={{ marginTop: 0 }}>
        Délai entre chaque email de rappel envoyé au validateur tant que la facture n&apos;est pas
        décidée. Par défaut : {effectiveCadenceDays} jours.
      </p>
      <div
        className="reminder-cadence__row"
        role="radiogroup"
        aria-label="Cadence"
        data-testid="settings-cadence-row"
      >
        {CADENCE_OPTIONS.map((opt) => {
          const isActive = opt.value === effectiveCadenceDays;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              className={`reminder-cadence__toggle${isActive ? ' is-active' : ''}`}
              disabled
              data-testid={`settings-cadence-${opt.value}`}
              title="Configuration cadence à venir — voir REPORT REQ-FE-029g"
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <label className="reminder-cadence__notify" data-testid="settings-cadence-notify">
        <input
          type="checkbox"
          checked={effectiveNotify}
          disabled
          readOnly
          data-testid="settings-cadence-notify-input"
        />
        <span>
          Notifier le comptable par défaut par email lorsqu&apos;une demande de validation passe
          au-delà du seuil de rappel.
        </span>
      </label>
      <p className="muted reminder-cadence__hint" data-testid="settings-cadence-hint">
        Cadence et notification configurables après ajout des champs
        <code> tenant.reminderCadenceDays </code> et
        <code> tenant.notifyComptableOnReminder </code> sur le contrat (REQ-BE à venir). La cadence
        affichée correspond aux valeurs par défaut côté serveur.
      </p>
    </article>
  );
}
